import { HANA_CLIENT_INFO, configFromClientInfo, createWorkCalendar, type WorkCalendar } from '@hana/work-calendar';

/** Ngày của dữ liệu mẫu board (23/10/2026). */
export const BOARD_DAY = '2026-10-23';

// Mint-TMS-Dispatch · dữ liệu mẫu lưới điều xe 23/10/2026 (đúng dòng + thứ tự board). Không có tiền.
// Nguồn thật về sau: TblTRFOrder (NEED-PLAN) + TblVehicleList + OpStopsJson qua DataSource. Giờ: số giờ trong ngày (0..24).

export interface Slot {
  /** số lệnh TRF… */
  id: string;
  customer: string;
  /** giờ bắt đầu / kết thúc trong ngày */
  from: number;
  to: number;
  /** tuyến đầy đủ cho tooltip */
  route: string;
}

export interface Lane {
  /** biển đầu kéo hoặc tên nhà xe ngoài */
  name: string;
  sub: string;
  /** dòng phụ đỏ (khoá kỹ thuật / quá hạn Finish) */
  warn?: boolean;
  outside?: boolean;
  /** khoá kỹ thuật: không gán được lệnh */
  locked?: boolean;
  slots: Slot[];
}

export interface Pending {
  id: string;
  customer: string;
  desc: string;
  route: string;
}

const s = (id: string, customer: string, from: number, to: number, route: string): Slot => ({ id, customer, from, to, route });

export const LANES: readonly Lane[] = [
  { name: '50E13970', sub: 'Chenglong H7 420Hp', slots: [s('TRF2610078', 'SAVITA', 6, 14, 'ICD PHUOC LONG 3 → Kho SAVITA → Cat Lai Giang Nam')] },
  { name: '50H75513', sub: 'Chenglong H7 420Hp', slots: [s('TRF2610066', 'TAMNGOC', 3, 8, 'Tân Cảng Mỹ Thủy 1 → CAT LAI')] },
  { name: '50H91408', sub: 'Chenglong H7 420Hp', slots: [s('TRFA2610062', 'SEAPRO', 1, 5, 'Tân Cảng Mỹ Thủy 1 → CAT LAI')] },
  { name: '50H60625', sub: 'khoá kỹ thuật · quá hạn BD2', warn: true, locked: true, slots: [] },
  { name: '50E39407', sub: 'LuxS 445HP', slots: [s('TRF2610086', 'COCOGROUP', 6, 9, 'Kho lạnh NECS → Kho lạnh NECS'), s('TRF2610061', 'GREENBUD', 9, 15, 'Kho lạnh NECS → CAT LAI')] },
  { name: '50E32294', sub: 'LuxS 445HP · 2 lệnh quá hạn Finish', warn: true, slots: [s('TRF2610085', 'COCOGROUP', 2, 6, 'PACIFIC REEFER → Kho lạnh NECS')] },
  { name: '50E88210', sub: 'Pro 450Hp', slots: [s('TRF2610065', 'TAMNGOC', 0, 5, 'PACIFIC REEFER → Cat Lai Giang Nam')] },
  { name: 'QUYNHVY', sub: 'nhà xe ngoài', outside: true, slots: [s('TRF2610028', 'GREENBUD', 3, 8, 'G Fortune Đồng An → CAT LAI')] },
  { name: 'ĐQM', sub: 'nhà xe ngoài', outside: true, slots: [s('TRF2610069', 'NEWWIND', 1, 6, 'G Fortune Tân Vạn 2 → CAT LAI')] },
  { name: 'CUONGQUOC', sub: 'nhà xe ngoài', outside: true, slots: [s('TRFA2610072', 'BSAFood', 1, 5, 'PHU HUNG DEPOT → CAT LAI'), s('TRF2610027', 'GREENBUD', 17, 21, 'G Fortune Đồng An → CAT LAI')] },
];

export const PENDING: readonly Pending[] = [
  { id: 'TRF2610079', customer: 'SKVietNam', desc: 'SL260639310 · CAT LAI → G Fortune Đồng An · 23/10 23:00', route: 'CAT LAI → G Fortune Đồng An' },
  { id: 'TRFA2610076', customer: 'MKAGRISEA', desc: 'SL260639190 · PHU HUNG DEPOT → CAT LAI TMNL · 28/10 01:00', route: 'PHU HUNG DEPOT → CAT LAI TMNL' },
  { id: 'TRFA2610075', customer: 'CLASSIC', desc: 'SE260639130 · CAT LAI → CAT LAI · 28/10 01:00', route: 'CAT LAI → CAT LAI' },
];

export const HOURS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22] as const;

const pad = (n: number) => String(n).padStart(2, '0');
export const hourLabel = (h: number): string => pad(h);

/** Vị trí thanh trên trục 24 giờ: % trái + % rộng (trừ 3px ở CSS). */
export function slotBox(slot: Pick<Slot, 'from' | 'to'>): { left: number; width: number } {
  return { left: (slot.from / 24) * 100, width: ((slot.to - slot.from) / 24) * 100 };
}

export const slotLabel = (slot: Slot): string => `${slot.id} · ${slot.customer}`;
export const slotTitle = (slot: Slot): string => `${slotLabel(slot)} · ${slot.route} · ${slot.from}:00–${slot.to}:00`;

/** Hai khung giờ có chồng nhau không (chạm đầu cuối không tính). */
export const overlaps = (a: Pick<Slot, 'from' | 'to'>, b: Pick<Slot, 'from' | 'to'>): boolean => a.from < b.to && b.from < a.to;

/** Giờ mở cửa mặc định của ngày làm việc (giờ nguyên, lấy ca đầu từ work-calendar / tblClientInfo; không cứng). Lưới điều độ là trục 24h (board), chỉ điểm bắt đầu cho xe trống lấy từ giờ làm việc. */
export function openHour(day: string, cal: WorkCalendar = createWorkCalendar(configFromClientInfo(HANA_CLIENT_INFO))): number {
  const first = cal.shiftsOf(day)[0];
  return first ? Math.floor(first[0] / 60) : 0;
}

/** Gán lệnh chờ vào xe: đặt ngay sau thanh cuối (hoặc giờ mở cửa `start` nếu xe trống), dài 4 giờ, cắt ở 24:00. null nếu xe khoá. */
export function assign(lanes: readonly Lane[], name: string, order: Pending, start: number = openHour(BOARD_DAY)): Lane[] | null {
  const lane = lanes.find((l) => l.name === name);
  if (!lane || lane.locked) return null;
  const last = lane.slots.reduce((m, x) => Math.max(m, x.to), 0);
  const from = lane.slots.length ? Math.min(last, 20) : start;
  const slot: Slot = { id: order.id, customer: order.customer, from, to: Math.min(from + 4, 24), route: order.route };
  return lanes.map((l) => (l.name === name ? { ...l, slots: [...l.slots, slot] } : l));
}

/** Gỡ lệnh khỏi lưới (Hoàn tác). */
export const unassign = (lanes: readonly Lane[], id: string): Lane[] => lanes.map((l) => ({ ...l, slots: l.slots.filter((x) => x.id !== id) }));

export const countAssigned = (lanes: readonly Lane[]): number => lanes.reduce((n, l) => n + l.slots.length, 0);
